import styles from "./tanksShowcase.module.css";

interface TanksShowcaseProps {
  lang: "tr" | "en";
}

// Bilingual labels for the decorative tank-dashboard mock. The page's indexed
// copy lives in landingPages.ts; these are supplementary visual labels.
const L = {
  tr: {
    caption: "Tank stok panelinin örnek görünümü",
    live: "CANLI",
    remaining: "Kalan stok",
    indicative: "tahmini",
    delivered: "teslim",
  },
  en: {
    caption: "Sample view of the tank stock dashboard",
    live: "LIVE",
    remaining: "Remaining stock",
    indicative: "indicative",
    delivered: "delivered",
  },
};

// Sample tanks (fill %, colour by fuel). Static, crawler-safe, no data fetch.
const TANKS = [
  { name: "T1", fuel: "VP Diesel", color: "#16a34a", fill: 81, volume: "8.102 L", delivery: "7.194 L" },
  { name: "T2", fuel: "VP95", color: "#dc2626", fill: 54, volume: "5.420 L", delivery: "" },
  { name: "T3", fuel: "LPG", color: "#9333ea", fill: 33, volume: "3.260 L", delivery: "" },
  { name: "T4", fuel: "AdBlue", color: "#0891b2", fill: 18, volume: "856 L", delivery: "" },
];

/**
 * A static, crawler-safe CSS mock of the tank stock dashboard, used as the
 * "screenshot" on the tank-monitoring landing page. No data fetching, so it
 * pre-renders cleanly.
 */
function TanksShowcase({ lang }: TanksShowcaseProps): JSX.Element {
  const t = L[lang];

  return (
    <figure className={styles.showcase} aria-label={t.caption}>
      <div className={styles.window}>
        <div className={styles.toolbar}>
          <div className={styles.dots}>
            <span />
            <span />
            <span />
          </div>
          <span className={styles.liveBadge}>
            <span className={styles.liveDot} />
            {t.live}
          </span>
        </div>

        <div className={styles.body}>
          <div className={styles.tankRow} aria-hidden="true">
            {TANKS.map((tk) => (
              <div key={tk.name} className={styles.tankCard}>
                <div className={styles.tank}>
                  <div
                    className={styles.tankFill}
                    style={{
                      height: `${tk.fill}%`,
                      background: `linear-gradient(180deg, ${tk.color}d9, ${tk.color})`,
                    }}
                  />
                  <span className={styles.tankPct}>{tk.fill}%</span>
                </div>
                <div className={styles.tankMeta}>
                  <span className={styles.tankFuel} style={{ color: tk.color }}>
                    ● {tk.fuel}
                  </span>
                  <strong className={styles.tankVolume}>{tk.volume}</strong>
                  {tk.delivery && (
                    <span className={styles.tankDelivery}>
                      ▲ {tk.delivery} {t.delivered}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.fuelStrip}>
            <span className={styles.fuelStripLabel}>
              {t.remaining}
              <span className={styles.indicative}>{t.indicative}</span>
            </span>
            <div className={styles.fuelBars}>
              {TANKS.map((tk) => (
                <div key={tk.name} className={styles.fuelBar}>
                  <span
                    className={styles.fuelBarFill}
                    style={{ width: `${tk.fill}%`, background: tk.color }}
                  />
                  <span className={styles.fuelBarName}>{tk.fuel}</span>
                  <span className={styles.fuelBarValue}>{tk.volume}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <figcaption className={styles.caption}>{t.caption}</figcaption>
    </figure>
  );
}

export default TanksShowcase;
